import { usePageRefresh } from '../../../context/PageRefreshContext';
// Task Management → Dashboard tab.
// Overall summary across every project and task in the society.
//
// Everything on this screen comes from one call: GET /api/tasks/dashboard
// (redux: getTaskDashboard → taskDashboardData). The API pre-computes every
// count, percentage and sort order, so nothing here re-derives them — see the
// "semantics" notes in the API docs (open excludes COMPLETED/CLOSED/CANCELLED,
// completed = COMPLETED + CLOSED, and so on).
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTaskDashboard, getProjectsList } from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import SearchableDropdown from '../../../components/SearchableDropdown';
import TaskTabs from './TaskTabs';
import {
    TASK_DEPARTMENTS, projectStatusBadgeClass,
    statusBarClass, priorityBarClass, humanize,
} from '../../../consts/taskConstants';

const num = (value) => (typeof value === 'number' ? value.toLocaleString('en-IN') : value ?? '—');
const initials = (name) => (name || '?').split(' ').map((w) => w.charAt(0)).slice(0, 2).join('').toUpperCase();
const pct = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

const cardCls = 'tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100';

// ---------------- building blocks ----------------

// Headline metric. `tone` colours the icon chip only — the number itself stays
// in ink so the value never depends on colour to be read.
const Stat = ({ icon, tone, value, label, hint, onClick }) => {
    const Wrapper = onClick ? 'button' : 'div';
    return (
        <Wrapper
            onClick={onClick}
            className={`${cardCls} tw-p-3 tw-flex tw-items-start tw-gap-2.5 tw-text-left tw-w-full ${onClick ? 'hover:tw-border-blue-200 hover:tw-shadow' : ''}`}
        >
            <div className={`tw-w-9 tw-h-9 tw-rounded-lg ${tone} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                <Icon icon={icon} className="tw-text-white tw-text-lg" />
            </div>
            <div className="tw-min-w-0">
                <p className="tw-m-0 tw-text-xl tw-font-bold tw-text-gray-800 tw-leading-none tw-tabular-nums">{num(value)}</p>
                <p className="tw-m-0 tw-mt-1 tw-text-[11px] tw-font-medium tw-text-gray-600">{label}</p>
                {hint && <p className="tw-m-0 tw-text-[10px] tw-text-gray-400 tw-truncate">{hint}</p>}
            </div>
        </Wrapper>
    );
};

const SectionCard = ({ title, subtitle, icon, action, children }) => (
    <div className={cardCls}>
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-100">
            <div>
                <h2 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-1.5">
                    {icon && <Icon icon={icon} className="tw-text-blue-600" />} {title}
                </h2>
                {subtitle && <p className="tw-m-0 tw-text-[11px] tw-text-gray-500">{subtitle}</p>}
            </div>
            {action}
        </div>
        <div className="tw-p-4">{children}</div>
    </div>
);

// One labelled bar. Label + count sit outside the track, so the bar carries
// magnitude only and nothing is encoded by colour alone. `percent` comes from
// the API already rounded.
const BarRow = ({ label, value, percent, barCls, onClick }) => (
    <div className={`tw-group ${onClick ? 'tw-cursor-pointer' : ''}`} onClick={onClick} title={`${label}: ${value} (${percent}%)`}>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
            <span className="tw-text-[11px] tw-font-medium tw-text-gray-600 group-hover:tw-text-gray-800">{label}</span>
            <span className="tw-text-[11px] tw-text-gray-500 tw-tabular-nums">
                <span className="tw-font-semibold tw-text-gray-700">{num(value)}</span> · {percent}%
            </span>
        </div>
        <div className="tw-h-1.5 tw-w-full tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
            <div className={`tw-h-full tw-rounded-full ${barCls}`} style={{ width: `${percent}%` }} />
        </div>
    </div>
);

// Completion ring — a single share of a whole, so one hue on a neutral track.
const Ring = ({ value, label, hint, hue = '#10b981' }) => {
    const r = 34;
    const c = 2 * Math.PI * r;
    const filled = Math.max(0, Math.min(100, value || 0));
    return (
        <div className="tw-flex tw-items-center tw-gap-3">
            <svg width="84" height="84" viewBox="0 0 84 84" className="tw-flex-shrink-0">
                <circle cx="42" cy="42" r={r} fill="none" stroke="#f1f5f9" strokeWidth="8" />
                <circle
                    cx="42" cy="42" r={r} fill="none" stroke={hue} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${(filled / 100) * c} ${c}`} transform="rotate(-90 42 42)"
                />
                <text x="42" y="46" textAnchor="middle" className="tw-fill-gray-800" style={{ fontSize: 16, fontWeight: 700 }}>
                    {filled}%
                </text>
            </svg>
            <div>
                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-800">{label}</p>
                {hint && <p className="tw-m-0 tw-text-[11px] tw-text-gray-500">{hint}</p>}
            </div>
        </div>
    );
};

const departmentOptions = TASK_DEPARTMENTS.map((d) => ({ value: d, label: humanize(d) }));

// ---------------- page ----------------

const TaskDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const {
        taskDashboardData, taskDashboardLoading,
        projectsListData, projectsListLoading,
    } = useSelector((s) => s.TaskReducer);

    // Every filter below maps 1:1 onto a documented query param and narrows
    // every section of the response.
    const [filterProject, setFilterProject] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {
        dispatch(getProjectsList({}));
    }, [dispatch]);

    const loadDashboard = useMemo(() => () => {
        dispatch(getTaskDashboard({
            project_id: filterProject || undefined,
            department: filterDepartment || undefined,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            assignee_limit: 10,
            project_limit: 20,
        }));
    }, [dispatch, filterProject, filterDepartment, fromDate, toDate]);
    usePageRefresh(loadDashboard);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const projectOptions = useMemo(
        () => (Array.isArray(projectsListData) ? projectsListData : []).map((p) => ({ value: String(p.project_id), label: p.name })),
        [projectsListData],
    );

    const d = taskDashboardData || {};
    const projects = d.projects || {};
    const tasks = d.tasks || {};
    const throughput = d.throughput || {};
    const byStatus = Array.isArray(d.by_status) ? d.by_status : [];
    const byPriority = Array.isArray(d.by_priority) ? d.by_priority : [];
    const byDepartment = Array.isArray(d.by_department) ? d.by_department : [];
    const projectRows = Array.isArray(d.project_breakdown) ? d.project_breakdown : [];
    const workload = Array.isArray(d.workload) ? d.workload : [];

    // Zero buckets are returned for completeness; drop them so the bar lists
    // stay scannable. Order within each array is meaningful — keep it.
    const statusRows = byStatus.filter((r) => r.count > 0);
    const departmentRows = byDepartment.filter((r) => r.count > 0);

    const maxWorkload = workload.reduce((m, a) => Math.max(m, a.total ?? 0), 0);

    const hasFilters = filterProject || filterDepartment || fromDate || toDate;
    const clearFilters = () => {
        setFilterProject('');
        setFilterDepartment('');
        setFromDate('');
        setToDate('');
    };

    const goToTasks = (query) => navigate(`/society-portal/tasks/list${query ? `?${query}` : ''}`);
    // Tile drill-downs carry the dashboard's own project/department scope along
    // so the list shows the same slice the number was counted from.
    const scoped = (query) => [
        query,
        filterProject ? `project_id=${filterProject}` : '',
        filterDepartment ? `department=${filterDepartment}` : '',
    ].filter(Boolean).join('&');

    const loading = taskDashboardLoading && !taskDashboardData;

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-flex-wrap tw-gap-2">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-500 tw-flex tw-items-center tw-justify-center tw-shadow-sm">
                        <Icon icon="mdi:view-dashboard-outline" className="tw-text-white tw-text-lg" />
                    </div>
                    <div>
                        <h1 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-gray-800">Dashboard</h1>
                        <p className="tw-m-0 tw-text-xs tw-text-gray-500">Overall summary of all projects and tasks</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={loadDashboard}
                        disabled={taskDashboardLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-border tw-border-gray-200 tw-text-gray-700 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-gray-50 disabled:tw-opacity-50"
                    >
                        <Icon icon="mdi:refresh" className={taskDashboardLoading ? 'tw-animate-spin' : ''} /> Refresh
                    </button>
                    {can(PERMISSIONS.TASK_CREATE) && (
                        <button
                            onClick={() => navigate('/society-portal/tasks/add')}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                        >
                            <Icon icon="mdi:plus" /> New Task
                        </button>
                    )}
                </div>
            </div>

            <TaskTabs active="dashboard" />

            {/* Filters — each one narrows every section below */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-w-[200px]">
                    <SearchableDropdown value={filterProject} onChange={(v) => setFilterProject(v === 'all' ? '' : v)}
                        options={projectOptions} loading={projectsListLoading} placeholder="All Projects" small />
                </div>
                <div className="tw-w-[170px]">
                    <SearchableDropdown value={filterDepartment} onChange={(v) => setFilterDepartment(v === 'all' ? '' : v)}
                        options={departmentOptions} placeholder="All Departments" small />
                </div>
                <div className="tw-flex tw-items-center tw-gap-1.5">
                    <label className="tw-text-[11px] tw-text-gray-500">Created</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate || undefined}
                        className="tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs focus:tw-outline-none focus:tw-border-blue-500" />
                    <span className="tw-text-gray-400 tw-text-xs">to</span>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate || undefined}
                        className="tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs focus:tw-outline-none focus:tw-border-blue-500" />
                </div>
                {hasFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
            </div>

            {loading ? (
                <div className={`${cardCls} tw-text-center tw-py-16`}>
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-gray-500">Loading dashboard...</p>
                </div>
            ) : !taskDashboardData ? (
                <div className={`${cardCls} tw-text-center tw-py-16`}>
                    <Icon icon="mdi:chart-box-outline" className="tw-text-4xl tw-text-gray-300" />
                    <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-gray-500">Summary unavailable</p>
                    <button onClick={loadDashboard} className="tw-mt-3 tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-underline">Try again</button>
                </div>
            ) : (
                <>
                    {/* Projects at a glance */}
                    <p className="tw-m-0 tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-400">Projects</p>
                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3 tw-mb-5">
                        <Stat icon="hugeicons:folder-01" tone="tw-bg-indigo-500" value={projects.total} label="Total Projects"
                            hint={`${num(projects.archived)} archived`} onClick={() => navigate('/society-portal/tasks/projects')} />
                        <Stat icon="mdi:play-circle-outline" tone="tw-bg-emerald-500" value={projects.active} label="Active Projects" />
                        <Stat icon="mdi:clipboard-text-clock-outline" tone="tw-bg-blue-500" value={projects.planning} label="In Planning" />
                        <Stat icon="mdi:pause-circle-outline" tone="tw-bg-orange-500" value={projects.on_hold} label="On Hold"
                            hint={`${num(projects.completed)} completed`} />
                        <Stat icon="mdi:alert-circle-outline" tone="tw-bg-red-500" value={projects.overdue} label="Overdue Projects"
                            hint="past due date, not completed" />
                    </div>

                    {/* Tasks at a glance */}
                    <p className="tw-m-0 tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-400">Tasks</p>
                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-3 tw-mb-3">
                        <Stat icon="hugeicons:task-01" tone="tw-bg-blue-600" value={tasks.total} label="Total Tasks"
                            hint={`${num(tasks.open)} still open`} onClick={() => goToTasks(scoped(''))} />
                        <Stat icon="mdi:progress-clock" tone="tw-bg-sky-500" value={tasks.in_progress} label="In Progress"
                            hint={`${tasks.in_progress_percent ?? 0}% of all tasks`} onClick={() => goToTasks(scoped('status=IN_PROGRESS'))} />
                        {/* Combined tiles drill down on every status they count —
                            the list API takes them comma separated. */}
                        <Stat icon="mdi:check-circle-outline" tone="tw-bg-emerald-500" value={tasks.completed} label="Completed"
                            hint={`${num(tasks.completed_only)} completed · ${num(tasks.closed)} closed`} onClick={() => goToTasks(scoped('status=COMPLETED,CLOSED'))} />
                        <Stat icon="mdi:clock-outline" tone="tw-bg-gray-500" value={tasks.pending} label="Pending"
                            hint="pending, assigned or accepted" onClick={() => goToTasks(scoped('status=PENDING,ASSIGNED,ACCEPTED'))} />
                        <Stat icon="mdi:alert-circle-outline" tone="tw-bg-red-500" value={tasks.overdue} label="Overdue"
                            hint={`${tasks.overdue_percent_of_open ?? 0}% of open tasks`} onClick={() => goToTasks(scoped('due=overdue'))} />
                        <Stat icon="mdi:restore-alert" tone="tw-bg-purple-500" value={tasks.reopened} label="Reopened"
                            hint="sent back after completion" onClick={() => goToTasks(scoped('status=REOPENED'))} />
                        <Stat icon="mdi:calendar-today" tone="tw-bg-amber-500" value={tasks.due_today} label="Due Today"
                            hint={`${num(tasks.due_this_week)} due this week`} onClick={() => goToTasks(scoped('due=today'))} />
                        <Stat icon="mdi:account-question-outline" tone="tw-bg-slate-500" value={tasks.unassigned} label="Unassigned"
                            hint="open, no assignee yet" />
                    </div>

                    {/* Secondary task metrics */}
                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-mb-5">
                        <Stat icon="mdi:timer-sand" tone="tw-bg-amber-600" value={tasks.waiting_approval} label="Waiting Approval"
                            onClick={() => goToTasks(scoped('status=WAITING_APPROVAL'))} />
                        <Stat icon="mdi:pause-circle-outline" tone="tw-bg-orange-500" value={tasks.on_hold} label="Tasks On Hold"
                            onClick={() => goToTasks(scoped('status=ON_HOLD'))} />
                        <Stat icon="mdi:close-circle-outline" tone="tw-bg-rose-500" value={tasks.rejected} label="Rejected"
                            onClick={() => goToTasks(scoped('status=REJECTED'))} />
                        <Stat icon="mdi:cancel" tone="tw-bg-gray-400" value={tasks.cancelled} label="Cancelled"
                            onClick={() => goToTasks(scoped('status=CANCELLED'))} />
                    </div>

                    {/* Throughput + distributions */}
                    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-4">
                        <SectionCard title="Throughput" subtitle="How much of the workload is closed out" icon="mdi:speedometer">
                            <div className="tw-space-y-4">
                                <Ring value={throughput.completion_rate} label="Completion rate"
                                    hint={`${num(throughput.completed)} of ${num(throughput.total)} tasks done`} />
                                <Ring value={throughput.on_time_rate} label="On-time rate" hue="#0ea5e9"
                                    hint={`${num(throughput.on_time_count)} of ${num(throughput.measured_against)} finished tasks with a due date`} />
                                <div className="tw-flex tw-items-center tw-gap-2 tw-pt-1 tw-border-t tw-border-gray-100">
                                    <Icon icon="mdi:timer-outline" className="tw-text-gray-400 tw-text-lg" />
                                    <div>
                                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-800">
                                            {throughput.avg_completion_days == null ? '—' : `${throughput.avg_completion_days} days`}
                                        </p>
                                        <p className="tw-m-0 tw-text-[11px] tw-text-gray-500">Average time to complete</p>
                                    </div>
                                </div>
                            </div>
                        </SectionCard>

                        <SectionCard title="Tasks by Status" subtitle="Every workflow state" icon="mdi:chart-timeline-variant"
                            action={<span className="tw-text-[11px] tw-text-gray-400 tw-tabular-nums">{num(tasks.total)} total</span>}>
                            <div className="tw-space-y-2.5">
                                {statusRows.length === 0
                                    ? <p className="tw-m-0 tw-text-sm tw-text-gray-400 tw-text-center tw-py-6">No tasks yet</p>
                                    : statusRows.map((row) => (
                                        <BarRow key={row.status} label={humanize(row.status)} value={row.count} percent={row.percent}
                                            barCls={statusBarClass(row.status)} onClick={() => goToTasks(scoped(`status=${row.status}`))} />
                                    ))}
                            </div>
                        </SectionCard>

                        <div className="tw-space-y-4">
                            <SectionCard title="Tasks by Priority" icon="mdi:flag-outline">
                                <div className="tw-space-y-2.5">
                                    {byPriority.length === 0
                                        ? <p className="tw-m-0 tw-text-sm tw-text-gray-400 tw-text-center tw-py-4">No tasks yet</p>
                                        : byPriority.map((row) => (
                                            <BarRow key={row.priority} label={humanize(row.priority)} value={row.count} percent={row.percent}
                                                barCls={priorityBarClass(row.priority)} onClick={() => goToTasks(scoped(`priority=${row.priority}`))} />
                                        ))}
                                </div>
                            </SectionCard>
                            <SectionCard title="Tasks by Department" icon="mdi:office-building-outline">
                                <div className="tw-space-y-2.5">
                                    {departmentRows.length === 0
                                        ? <p className="tw-m-0 tw-text-sm tw-text-gray-400 tw-text-center tw-py-4">No department data</p>
                                        : departmentRows.map((row) => (
                                            <BarRow key={row.department} label={humanize(row.department)} value={row.count} percent={row.percent}
                                                barCls="tw-bg-indigo-500"
                                                // UNSPECIFIED is a bucket, not a filterable department.
                                                onClick={row.department === 'UNSPECIFIED' ? undefined : () => goToTasks(`department=${row.department}`)} />
                                        ))}
                                </div>
                            </SectionCard>
                        </div>
                    </div>

                    {/* Per-project breakdown */}
                    <div className="tw-mb-4">
                        <SectionCard title="Project Breakdown" subtitle="Busiest projects first" icon="hugeicons:folder-01"
                            action={
                                <button onClick={() => navigate('/society-portal/tasks/projects')}
                                    className="tw-text-[11px] tw-font-medium tw-text-blue-600 hover:tw-underline">
                                    View all projects
                                </button>
                            }>
                            <div className="tw-overflow-x-auto">
                                <table className="tw-w-full tw-text-sm tw-border-collapse">
                                    <thead>
                                        <tr className="tw-bg-gray-50/80 tw-border-b tw-border-gray-200">
                                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Project</th>
                                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Tasks</th>
                                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Pending</th>
                                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">In Progress</th>
                                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Completed</th>
                                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Overdue</th>
                                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Reopened</th>
                                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[140px]">Progress</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tw-divide-y tw-divide-gray-100">
                                        {projectRows.map((p) => (
                                            <tr key={p.project_id ?? 'none'} className="hover:tw-bg-blue-50/40">
                                                <td className="tw-px-3 tw-py-1.5 tw-max-w-[240px]">
                                                    {p.project_id
                                                        ? (
                                                            <button onClick={() => navigate(`/society-portal/tasks/projects/${p.project_id}`)}
                                                                className="tw-font-medium tw-text-gray-800 tw-truncate hover:tw-text-blue-600 tw-text-left">
                                                                {p.name}
                                                            </button>
                                                        )
                                                        : <span className="tw-text-gray-500 tw-italic">{p.name}</span>}
                                                </td>
                                                <td className="tw-px-3 tw-py-1.5">
                                                    {p.status
                                                        ? <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium ${projectStatusBadgeClass(p.status)}`}>{humanize(p.status)}</span>
                                                        : <span className="tw-text-gray-300">—</span>}
                                                </td>
                                                <td className="tw-px-3 tw-py-1.5 tw-text-right tw-font-semibold tw-text-gray-800 tw-tabular-nums">{num(p.tasks)}</td>
                                                <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-tabular-nums">{num(p.pending)}</td>
                                                <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-tabular-nums">{num(p.in_progress)}</td>
                                                <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-tabular-nums">{num(p.completed)}</td>
                                                <td className="tw-px-3 tw-py-1.5 tw-text-right tw-tabular-nums">
                                                    {p.overdue > 0
                                                        ? <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-font-medium tw-text-red-600"><Icon icon="mdi:alert-circle-outline" className="tw-text-xs" />{num(p.overdue)}</span>
                                                        : <span className="tw-text-gray-300">0</span>}
                                                </td>
                                                <td className="tw-px-3 tw-py-1.5 tw-text-right tw-tabular-nums">
                                                    {p.reopened > 0
                                                        ? <span className="tw-font-medium tw-text-purple-600">{num(p.reopened)}</span>
                                                        : <span className="tw-text-gray-300">0</span>}
                                                </td>
                                                <td className="tw-px-3 tw-py-1.5">
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <div className="tw-flex-1 tw-h-1.5 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden tw-min-w-[60px]">
                                                            <div className="tw-h-full tw-bg-emerald-500 tw-rounded-full" style={{ width: `${p.progress_percent ?? 0}%` }} />
                                                        </div>
                                                        <span className="tw-text-[11px] tw-text-gray-500 tw-tabular-nums tw-w-8 tw-text-right">{p.progress_percent ?? 0}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {projectRows.length === 0 && (
                                    <div className="tw-text-center tw-py-8">
                                        <Icon icon="hugeicons:folder-01" className="tw-text-3xl tw-text-gray-300" />
                                        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-gray-500">No projects yet</p>
                                    </div>
                                )}
                            </div>
                        </SectionCard>
                    </div>

                    {/* Workload */}
                    <SectionCard title="Workload by Assignee" subtitle="Open vs completed tasks per person" icon="mdi:account-group-outline">
                        {workload.length === 0 ? (
                            <p className="tw-m-0 tw-text-sm tw-text-gray-400 tw-text-center tw-py-6">No assignment data</p>
                        ) : (
                            <>
                                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3 tw-text-[11px] tw-text-gray-500">
                                    <span className="tw-inline-flex tw-items-center tw-gap-1"><span className="tw-w-2.5 tw-h-2.5 tw-rounded-sm tw-bg-sky-500" /> Open</span>
                                    <span className="tw-inline-flex tw-items-center tw-gap-1"><span className="tw-w-2.5 tw-h-2.5 tw-rounded-sm tw-bg-emerald-500" /> Completed</span>
                                    <span className="tw-inline-flex tw-items-center tw-gap-1"><Icon icon="mdi:alert-circle-outline" className="tw-text-red-500" /> Overdue</span>
                                </div>
                                <div className="tw-space-y-3">
                                    {workload.map((a) => {
                                        const width = pct(a.total ?? 0, maxWorkload);
                                        const openShare = pct(a.open ?? 0, a.total ?? 0);
                                        return (
                                            <div key={a.user_id} className="tw-flex tw-items-center tw-gap-3">
                                                <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-teal-100 tw-text-teal-700 tw-text-[10px] tw-font-bold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                    {initials(a.name)}
                                                </span>
                                                <div className="tw-min-w-0 tw-flex-1">
                                                    <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2 tw-mb-1">
                                                        <button onClick={() => goToTasks(scoped(`assigned_to=${a.user_id}`))}
                                                            className="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate hover:tw-text-blue-600"
                                                            title={a.email || a.name}>
                                                            {a.name}
                                                        </button>
                                                        <span className="tw-text-[11px] tw-text-gray-500 tw-tabular-nums tw-flex-shrink-0">
                                                            {num(a.open)} open · {num(a.completed)} done
                                                            {a.overdue > 0 && (
                                                                <span className="tw-text-red-600 tw-font-medium"> · {num(a.overdue)} overdue</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {/* One track per person, split open / completed — 2px gap so the
                                                        two segments never read as a single fill. */}
                                                    <div className="tw-h-1.5 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden" style={{ width: `${width}%`, minWidth: '40px' }}>
                                                        <div className="tw-flex tw-h-full tw-gap-[2px]">
                                                            <div className="tw-h-full tw-bg-sky-500 tw-rounded-full" style={{ width: `${openShare}%` }} />
                                                            <div className="tw-h-full tw-bg-emerald-500 tw-rounded-full" style={{ width: `${100 - openShare}%` }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </SectionCard>
                </>
            )}
        </>
    );
};

export default TaskDashboard;
